import { useEffect, useState } from "react";
import { api, useApi } from "../api.js";
import { ActionButton, Notice, useToast } from "../components.js";

interface SettingsData {
  aiProvider: "simulated" | "openai";
  workerEnabled: boolean;
  workerConcurrency: number;
  maxRetry: number;
  /** Null means no override — BATCH_GENERATION_ENABLED in the environment
   *  decides. Only an explicit true/false here overrides it. */
  batchGenerationEnabled: boolean | null;
  batchCollectionIds: string | null;
  batchMaxJobs: number | null;
  temperature: number | null;
  topP: number | null;
  frequencyPenalty: number | null;
  presencePenalty: number | null;
  maxTokens: number | null;
  reasoningEffort: "low" | "medium" | "high" | null;
  seed: number | null;
  updatedAt: string;
  worker: { running: boolean; active: number; processed: number; succeeded: number; failed: number };
}

/** The tri-state the batch toggle exposes: defer to the environment, or
 *  override it in either direction. */
type BatchMode = "env" | "on" | "off";

function toBatchMode(value: boolean | null | undefined): BatchMode {
  if (value === true) return "on";
  if (value === false) return "off";
  return "env";
}

/**
 * The operational knobs that used to require an .env edit and a restart:
 * which AI provider is live, whether the worker runs, its concurrency, and
 * the retry ceiling. A change here takes effect for the next job the worker
 * picks up — nothing already in flight is affected.
 */
export function Settings() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [togglingWorker, setTogglingWorker] = useState(false);
  const { toast, element } = useToast();

  const settings = useApi<SettingsData>("/settings", [refreshKey]);
  const s = settings.data;

  const [aiProvider, setAiProvider] = useState<"simulated" | "openai">("simulated");
  const [workerConcurrency, setWorkerConcurrency] = useState(5);
  const [maxRetry, setMaxRetry] = useState(3);
  const [batchMode, setBatchMode] = useState<BatchMode>("env");
  const [batchCollectionIds, setBatchCollectionIds] = useState("");
  const [batchMaxJobs, setBatchMaxJobs] = useState("");
  const [temperature, setTemperature] = useState<number>(0.7);
  const [topP, setTopP] = useState<number>(1.0);
  const [frequencyPenalty, setFrequencyPenalty] = useState<number>(0.0);
  const [presencePenalty, setPresencePenalty] = useState<number>(0.0);
  const [maxTokens, setMaxTokens] = useState<string>("");
  const [reasoningEffort, setReasoningEffort] = useState<"low" | "medium" | "high">("medium");
  const [seed, setSeed] = useState<string>("");

  // Sync the editable fields whenever fresh data arrives
  useEffect(() => {
    if (!s) return;
    setAiProvider(s.aiProvider);
    setWorkerConcurrency(s.workerConcurrency);
    setMaxRetry(s.maxRetry);
    setBatchMode(toBatchMode(s.batchGenerationEnabled));
    setBatchCollectionIds(s.batchCollectionIds ?? "");
    setBatchMaxJobs(s.batchMaxJobs === null ? "" : String(s.batchMaxJobs));
    setTemperature(s.temperature ?? 0.7);
    setTopP(s.topP ?? 1.0);
    setFrequencyPenalty(s.frequencyPenalty ?? 0.0);
    setPresencePenalty(s.presencePenalty ?? 0.0);
    setMaxTokens(s.maxTokens === null ? "" : String(s.maxTokens));
    setReasoningEffort(s.reasoningEffort ?? "medium");
    setSeed(s.seed === null ? "" : String(s.seed));
  }, [s]);

  useEffect(() => {
    const timer = setInterval(() => setRefreshKey((k) => k + 1), 5000);
    return () => clearInterval(timer);
  }, []);

  async function save() {
    setSaving(true);
    try {
      await api.put("/settings", {
        aiProvider,
        workerConcurrency,
        maxRetry,
        // null clears the override and hands control back to the environment.
        batchGenerationEnabled: batchMode === "env" ? null : batchMode === "on",
        batchCollectionIds: batchMode === "env" ? null : batchCollectionIds.trim(),
        batchMaxJobs: batchMaxJobs.trim() === "" ? null : Number(batchMaxJobs),
        temperature,
        topP,
        frequencyPenalty,
        presencePenalty,
        maxTokens: maxTokens.trim() === "" ? null : Number(maxTokens),
        reasoningEffort,
        seed: seed.trim() === "" ? null : Number(seed),
      });
      toast("Settings saved");
      setRefreshKey((k) => k + 1);
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function toggleWorker() {
    if (!s) return;
    setTogglingWorker(true);
    try {
      await api.put("/settings", { workerEnabled: !s.workerEnabled });
      toast(s.workerEnabled ? "Worker stopped" : "Worker started");
      setRefreshKey((k) => k + 1);
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Failed");
    } finally {
      setTogglingWorker(false);
    }
  }

  const applyPreset = (preset: "derivation" | "mcq" | "prose" | "critique" | "schema" | "default") => {
    if (preset === "default") {
      setTemperature(1.0);
      setTopP(1.0);
      setFrequencyPenalty(0.0);
      setPresencePenalty(0.0);
      setReasoningEffort("medium");
      setSeed("");
      setMaxTokens("");
      toast("Reset hyperparameters to OpenAI API defaults");
      return;
    }
    if (preset === "derivation") {
      setTemperature(0.0);
      setTopP(1.0);
      setFrequencyPenalty(0.0);
      setPresencePenalty(0.0);
      setReasoningEffort("high");
      setSeed("42");
      setMaxTokens("2000");
    } else if (preset === "mcq") {
      setTemperature(0.2);
      setTopP(1.0);
      setFrequencyPenalty(0.1);
      setPresencePenalty(0.0);
      setSeed("42");
      setMaxTokens("1000");
    } else if (preset === "prose") {
      setTemperature(0.6);
      setTopP(0.9);
      setFrequencyPenalty(0.1);
      setPresencePenalty(0.2);
      setSeed("");
      setMaxTokens("1500");
    } else if (preset === "critique") {
      setTemperature(0.0);
      setTopP(1.0);
      setFrequencyPenalty(0.0);
      setPresencePenalty(0.0);
      setReasoningEffort("medium");
      setSeed("42");
      setMaxTokens("800");
    } else if (preset === "schema") {
      setTemperature(0.0);
      setTopP(1.0);
      setFrequencyPenalty(0.0);
      setPresencePenalty(0.0);
      setSeed("42");
      setMaxTokens("3000");
    }
    toast(`Applied preset: ${preset}`);
  };

  const dirty =
    s &&
    (aiProvider !== s.aiProvider ||
      workerConcurrency !== s.workerConcurrency ||
      maxRetry !== s.maxRetry ||
      batchMode !== toBatchMode(s.batchGenerationEnabled) ||
      batchCollectionIds !== (s.batchCollectionIds ?? "") ||
      batchMaxJobs !== (s.batchMaxJobs === null ? "" : String(s.batchMaxJobs)) ||
      temperature !== (s.temperature ?? 0.7) ||
      topP !== (s.topP ?? 1.0) ||
      frequencyPenalty !== (s.frequencyPenalty ?? 0.0) ||
      presencePenalty !== (s.presencePenalty ?? 0.0) ||
      maxTokens !== (s.maxTokens === null ? "" : String(s.maxTokens)) ||
      reasoningEffort !== (s.reasoningEffort ?? "medium") ||
      seed !== (s.seed === null ? "" : String(s.seed)));
  const switchingToRealSpend = s && aiProvider === "openai" && s.aiProvider !== "openai";

  return (
    <>
      {settings.error && <Notice kind="error">Cannot load settings: {settings.error}</Notice>}

      <div className="grid">
        <div className="card span-7">
          <h2>AI provider</h2>
          <div className="field">
            <label>Provider</label>
            <select value={aiProvider} onChange={(e) => setAiProvider(e.target.value as "simulated" | "openai")}>
              <option value="simulated">Simulated — no network, no cost, fake output</option>
              <option value="openai">OpenAI — real requests, real spend</option>
            </select>
          </div>

          {aiProvider === "openai" && (
            <Notice kind="warn">
              <strong>Real money.</strong> Every generation, critique, and Run-tab test will call
              OpenAI and be billed once this is saved. Applies to the next job the worker picks up
              and every request made from here on — nothing already in progress is affected.
            </Notice>
          )}

          {switchingToRealSpend && (
            <p className="muted mini">Currently: {s.aiProvider}. Save to switch to openai.</p>
          )}

          <h2 style={{ marginTop: 24 }}>Worker</h2>
          <div className="row" style={{ gap: 16, alignItems: "center", marginBottom: 12 }}>
            <span className={`badge ${s?.workerEnabled ? "generated" : "pending"}`}>
              {s?.workerEnabled ? "Running" : "Stopped"}
            </span>
            {s && (
              <span className="mini muted">
                {s.worker.active} active · {s.worker.succeeded} succeeded · {s.worker.failed} failed since
                start
              </span>
            )}
            <ActionButton
              className="mini"
              pending={togglingWorker}
              disabled={!s}
              onClick={() => void toggleWorker()}
            >
              {s?.workerEnabled ? "Stop worker" : "Start worker"}
            </ActionButton>
          </div>

          <div className="field">
            <label>Concurrency</label>
            <input
              type="number"
              min={1}
              max={50}
              value={workerConcurrency}
              onChange={(e) => setWorkerConcurrency(Number(e.target.value))}
              style={{ width: 100 }}
            />
            <span className="mini muted">How many jobs the worker runs at once.</span>
          </div>

          <div className="field">
            <label>Max retry</label>
            <input
              type="number"
              min={1}
              max={20}
              value={maxRetry}
              onChange={(e) => setMaxRetry(Number(e.target.value))}
              style={{ width: 100 }}
            />
            <span className="mini muted">
              Retryable failures (rate limits, timeouts) are attempted this many times before a job
              is marked FAILED.
            </span>
          </div>

          <div className="field">
            <label>Batch generation</label>
            <select value={batchMode} onChange={(e) => setBatchMode(e.target.value as BatchMode)}>
              <option value="env">Use environment setting</option>
              <option value="on">On — generate through the Batch API</option>
              <option value="off">Off — always synchronous</option>
            </select>
            <span className="mini muted">
              Batch costs about half as much per token but takes minutes to hours instead of
              seconds, and OpenAI only guarantees completion within 24 hours. Critique always runs
              synchronously. &quot;Use environment setting&quot; defers to BATCH_GENERATION_ENABLED.
            </span>
          </div>

          {batchMode !== "env" && (
            <>
              <div className="field">
                <label>Batch collections</label>
                <input
                  value={batchCollectionIds}
                  onChange={(e) => setBatchCollectionIds(e.target.value)}
                  placeholder="e.g. 8"
                  style={{ width: 160 }}
                />
                <span className="mini muted">
                  Comma-separated collection ids. <b>Empty means every collection</b> — set one id
                  while trying batch out.
                </span>
              </div>

              <div className="field">
                <label>Jobs per batch</label>
                <input
                  type="number"
                  min={1}
                  max={2000}
                  value={batchMaxJobs}
                  onChange={(e) => setBatchMaxJobs(e.target.value)}
                  placeholder="20"
                  style={{ width: 100 }}
                />
                <span className="mini muted">
                  Maximum, not a target — a lone pending job is submitted on its own rather than
                  waiting for company. Blank uses the environment default.
                </span>
              </div>
            </>
          )}

          {batchMode === "on" && batchCollectionIds.trim() === "" && (
            <Notice kind="warn">
              With no collection ids, <b>every</b> collection will generate through Batch.
            </Notice>
          )}

          <div className="row" style={{ marginTop: 16 }}>
            <button className="btn primary" onClick={() => void save()} disabled={!dirty || saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>

          {s && (
            <p className="muted mini" style={{ marginTop: 12 }}>
              Last changed {new Date(s.updatedAt).toLocaleString()}. Takes effect for the next job
              the worker picks up — nothing already running is interrupted.
            </p>
          )}
        </div>

        <div className="card span-5">
          <h2>🤖 OpenAI Model Hyperparameters</h2>
          <p className="mini muted" style={{ marginBottom: 12 }}>
            Tune sampling and repetition parameters applied to all model generation calls.
          </p>

          <label style={{ fontSize: "0.85rem", fontWeight: 600, display: "block", marginBottom: 6 }}>
            Quick Task Presets
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16, alignItems: "center" }}>
            <button type="button" className="btn mini" onClick={() => applyPreset("derivation")}>🔬 Strict Derivation</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("mcq")}>❓ MCQ Question Gen</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("prose")}>📖 Conceptual Prose</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("critique")}>🔍 Rubric Critique</button>
            <button type="button" className="btn mini" onClick={() => applyPreset("schema")}>⚡ Schema Validation</button>
            <button type="button" className="btn mini" style={{ backgroundColor: "var(--border)", color: "var(--text)" }} onClick={() => applyPreset("default")}>🔄 Default (OpenAI Standard)</button>
          </div>

          <div className="field">
            <div className="row between" style={{ alignItems: "center" }}>
              <label style={{ margin: 0 }}>Temperature</label>
              <input
                type="number"
                step="0.05"
                min="0.0"
                max="2.0"
                value={temperature}
                onChange={(e) => setTemperature(Number(e.target.value))}
                style={{ width: 80, textAlign: "right" }}
              />
            </div>
            <input
              type="range"
              min="0.0"
              max="2.0"
              step="0.05"
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              style={{ width: "100%", marginTop: 6 }}
            />
            <span className="mini muted">
              Controls randomness (0.0 = deterministic/focused, 1.0 = creative, 2.0 = random).
            </span>
          </div>

          <div className="field">
            <div className="row between" style={{ alignItems: "center" }}>
              <label style={{ margin: 0 }}>Top P (Nucleus Sampling)</label>
              <input
                type="number"
                step="0.05"
                min="0.0"
                max="1.0"
                value={topP}
                onChange={(e) => setTopP(Number(e.target.value))}
                style={{ width: 80, textAlign: "right" }}
              />
            </div>
            <input
              type="range"
              min="0.0"
              max="1.0"
              step="0.05"
              value={topP}
              onChange={(e) => setTopP(Number(e.target.value))}
              style={{ width: "100%", marginTop: 6 }}
            />
            <span className="mini muted">
              Considers tokens comprising the top P probability mass (1.0 = consider all tokens).
            </span>
          </div>

          <div className="field">
            <div className="row between" style={{ alignItems: "center" }}>
              <label style={{ margin: 0 }}>Frequency Penalty</label>
              <input
                type="number"
                step="0.1"
                min="-2.0"
                max="2.0"
                value={frequencyPenalty}
                onChange={(e) => setFrequencyPenalty(Number(e.target.value))}
                style={{ width: 80, textAlign: "right" }}
              />
            </div>
            <input
              type="range"
              min="-2.0"
              max="2.0"
              step="0.1"
              value={frequencyPenalty}
              onChange={(e) => setFrequencyPenalty(Number(e.target.value))}
              style={{ width: "100%", marginTop: 6 }}
            />
            <span className="mini muted">
              Penalizes verbatim repetition based on token frequency (-2.0 to 2.0).
            </span>
          </div>

          <div className="field">
            <div className="row between" style={{ alignItems: "center" }}>
              <label style={{ margin: 0 }}>Presence Penalty</label>
              <input
                type="number"
                step="0.1"
                min="-2.0"
                max="2.0"
                value={presencePenalty}
                onChange={(e) => setPresencePenalty(Number(e.target.value))}
                style={{ width: 80, textAlign: "right" }}
              />
            </div>
            <input
              type="range"
              min="-2.0"
              max="2.0"
              step="0.1"
              value={presencePenalty}
              onChange={(e) => setPresencePenalty(Number(e.target.value))}
              style={{ width: "100%", marginTop: 6 }}
            />
            <span className="mini muted">
              Encourages introduction of new topics based on token presence (-2.0 to 2.0).
            </span>
          </div>

          <div className="field">
            <label>Reasoning Effort <span className="badge info mini">o1 / o3 models only</span></label>
            <select value={reasoningEffort} onChange={(e) => setReasoningEffort(e.target.value as "low" | "medium" | "high")}>
              <option value="low">low — faster, cheaper reasoning</option>
              <option value="medium">medium — balanced reasoning depth</option>
              <option value="high">high — max reasoning depth for complex science derivations</option>
            </select>
            <span className="mini muted">
              Controls internal thinking token budget on OpenAI reasoning models (o1, o3, o3-mini). Automatically stripped for gpt-4o.
            </span>
          </div>

          <div className="field">
            <label>Seed (Reproducibility)</label>
            <input
              type="number"
              placeholder="e.g. 42 (or blank for random)"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              style={{ width: "100%" }}
            />
            <span className="mini muted">
              Integer seed for reproducible sampling across batch runs.
            </span>
          </div>

          <div className="field">
            <label>Max Completion Tokens</label>
            <input
              type="number"
              min="1"
              max="128000"
              placeholder="Model Default (e.g. 4096)"
              value={maxTokens}
              onChange={(e) => setMaxTokens(e.target.value)}
              style={{ width: "100%" }}
            />
            <span className="mini muted">
              Upper limit on generated completion tokens per request. Leave empty for default.
            </span>
          </div>

          <div className="row" style={{ marginTop: 16 }}>
            <button className="btn primary" onClick={() => void save()} disabled={!dirty || saving}>
              {saving ? "Saving…" : "Save Parameters"}
            </button>
          </div>
        </div>
      </div>

      <PromptTemplates toast={toast} />

      {element}
    </>
  );
}

interface PromptTemplateRow {
  id: number;
  key: string;
  name: string;
  description: string | null;
  body: string;
  updatedAt: string;
}

/**
 * Global prompts — the ones that belong to no collection or syllabus.
 *
 * Edit-only: templates are seeded by migration and looked up by a key the
 * code references, so creating or deleting one here would break the caller
 * rather than configure anything.
 */
function PromptTemplates({ toast }: { toast: (msg: string) => void }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const templates = useApi<PromptTemplateRow[]>("/prompt-templates", [refreshKey]);

  async function save(key: string) {
    setSaving(true);
    try {
      await api.put(`/prompt-templates/${key}`, { body });
      toast("Prompt saved");
      setEditingKey(null);
      setRefreshKey((k) => k + 1);
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!templates.data || templates.data.length === 0) return null;

  return (
    <div className="grid">
      <div className="card span-12">
        <h3
          style={{
            margin: "0 0 4px",
            fontSize: 13,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--muted)",
          }}
        >
          Global prompts
        </h3>
        <p className="mini muted" style={{ marginTop: 0 }}>
          Used across every collection and syllabus.
        </p>

        {templates.data.map((t) => (
          <div key={t.key} className="book" style={{ marginTop: 10 }}>
            <div className="row between">
              <div>
                <b>{t.name}</b>
                {t.description && <div className="mini muted">{t.description}</div>}
              </div>
              {editingKey === t.key ? (
                <div className="row" style={{ gap: 6 }}>
                  <button
                    className="btn primary mini"
                    disabled={saving}
                    onClick={() => void save(t.key)}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button className="btn mini" disabled={saving} onClick={() => setEditingKey(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="btn mini"
                  onClick={() => {
                    setBody(t.body);
                    setEditingKey(t.key);
                  }}
                >
                  Edit
                </button>
              )}
            </div>

            {editingKey === t.key ? (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                style={{ marginTop: 8, minHeight: 200, width: "100%" }}
              />
            ) : (
              <pre className="preview mini" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                {t.body}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
